import { useEffect, useState } from "react";

import { Button, Callout, InputGroup, Intent } from "@blueprintjs/core";
import { Link } from "lucide-react";

import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { Box, Flex } from "@/components/layout/flexbox";
import { StyledFormGroup } from "@/components/pages_logged_in/program/components/set-components/StyledFormGroup";
import { Tiptap } from "@/components/pieces/editor/tiptap";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { EvaluationCriterionItem } from "@/types/roleplays";

interface UpdateBehaviorItemDialogProps {
  open: boolean;
  onClose: () => void;
  item: EvaluationCriterionItem | null;
  onSave: (
    itemId: string,
    item: {
      name?: string;
      description?: string;
      detailedInstructions?: string;
    },
  ) => void;
  loading?: boolean;
  errorMessage?: string;
}

export function UpdateBehaviorItemDialog({
  open,
  onClose,
  item,
  onSave,
  loading = false,
  errorMessage = "",
}: UpdateBehaviorItemDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [detailedInstructions, setDetailedInstructions] = useState("");
  const [showFullDescription, setShowFullDescription] = useState(false);

  useEffect(() => {
    if (open && item) {
      setName(item.name);
      setDescription(item.description || "");
      setDetailedInstructions(item.detailedInstructions || "");
      setShowFullDescription(false);
    }
  }, [open, item]);

  const handleClose = () => {
    // Reset state when closing
    setName("");
    setDescription("");
    setDetailedInstructions("");
    setShowFullDescription(false);
    onClose();
  };

  const handleSave = () => {
    if (item) {
      const updates: {
        name?: string;
        description?: string;
        detailedInstructions?: string;
      } = {};

      // For linked items, only send fields that can be edited
      if (!item.isLinked) {
        updates.name = name.trim();
        updates.description = description;
      } else {
        // For linked items, only detailed instructions can be edited
        updates.detailedInstructions = detailedInstructions;
      }

      onSave(item.id, updates);
    }
  };

  const isLinked = item?.isLinked || false;

  return (
    <StyledDialog
      isOpen={open}
      onClose={handleClose}
      lazy={true}
      title={<Box fontSize={18}>Edit Behavior Item</Box>}
      width="650px"
    >
      <>
        <div className="flex flex-col px-6 gap-6 py-4">
          {errorMessage && (
            <Callout icon={null} intent={Intent.DANGER}>
              {errorMessage}
            </Callout>
          )}

          {isLinked && item?.sourceRubric && (
            <div className="rounded-md bg-blue-200 p-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2 font-semibold text-blue-800">
                  <a
                    href={`/rubrics/${item.sourceRubric.uuid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-800 hover:text-blue-900"
                  >
                    <Link size={16} /> {item.sourceRubric.name}
                  </a>
                </div>
                <div className="text-sm text-blue-800/80">
                  Core content below is managed by the rubric and will sync
                  automatically
                </div>
              </div>
            </div>
          )}

          <StyledFormGroup
            label="Behavior Label"
            subLabel={isLinked ? undefined : "Describe what success looks like"}
          >
            {isLinked ? (
              <div className="rounded-md text-base">{item?.name}</div>
            ) : (
              <InputGroup
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Restate customer concerns"
                large
                fill
                autoFocus
              />
            )}
          </StyledFormGroup>

          <StyledFormGroup
            label="Evaluation Guide"
            subLabel={
              isLinked
                ? undefined
                : "Help our AI understand how to evaluate this behavior. Be specific and concrete about what success looks like."
            }
          >
            {isLinked ? (
              <div>
                <div className={!showFullDescription ? "line-clamp-5" : ""}>
                  <RichContentRenderer content={item?.description || ""} />
                </div>
                {item?.description && item.description.length > 300 && (
                  <Button
                    minimal
                    intent="primary"
                    small
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="mt-2"
                  >
                    {showFullDescription ? "See Less" : "See More"}
                  </Button>
                )}
              </div>
            ) : (
              <Tiptap
                key={item?.id || "empty"}
                content={description}
                onChange={(value) => setDescription(value)}
                placeholder="Break down how to evaluate this behavior. Include both what to look for and what to avoid."
              />
            )}
          </StyledFormGroup>

          {isLinked && (
            <StyledFormGroup
              label="Scenario-Specific Instructions"
              subLabel="Add instructions that are specific to this scenario. These will be combined with the evaluation guide above."
            >
              <Tiptap
                key={`${item?.id}-detailed`}
                content={detailedInstructions}
                onChange={(value) => setDetailedInstructions(value)}
                placeholder="e.g., 'For this healthcare client, pay special attention to HIPAA compliance mentions'"
              />
            </StyledFormGroup>
          )}
        </div>

        <Flex className="border-border sticky bottom-0 z-10 w-full rounded-b-xl border-t bg-white">
          <Box className="bbs" />
          <Box py={3} px={24}>
            <Button
              intent={Intent.PRIMARY}
              onClick={handleSave}
              loading={loading}
              disabled={!name.trim()}
              large
              style={{ minWidth: 118 }}
            >
              Save Changes
            </Button>
            <Button onClick={handleClose} large className="ml-2">
              Cancel
            </Button>
          </Box>
        </Flex>
      </>
    </StyledDialog>
  );
}
